import { fmtNumber, fmtCurrency } from '../../../i18n/format.js';
import { getLocale } from '../../../i18n/runtime.js';
/**
 * Utilities cho approval form builder.
 */

/**
 * Đọc số tiền VN ra chữ. VD: 1500000 → "Một triệu năm trăm nghìn đồng"
 * Hỗ trợ tới hàng tỷ tỷ (số rất lớn).
 */
// i18n-data: từ đọc số tiếng Việt — «đọc số tiền thành chữ» chỉ bật cho tiền VND (FieldDisplay/FieldInput).
const ONES = ['không', 'một', 'hai', 'ba', 'bốn', 'năm', 'sáu', 'bảy', 'tám', 'chín'];

function readThree(n, full) {
  const a = Math.floor(n / 100);
  const b = Math.floor((n % 100) / 10);
  const c = n % 10;
  let s = '';
  if (a > 0 || full) {
    s += ONES[a] + ' trăm';
    if (b === 0 && c > 0) s += ' linh';
  }
  if (b > 1) {
    s += (s ? ' ' : '') + ONES[b] + ' mươi';
    if (c === 1) s += ' mốt';
    else if (c === 5) s += ' lăm';
    else if (c > 0) s += ' ' + ONES[c];
  } else if (b === 1) {
    s += (s ? ' ' : '') + 'mười';
    if (c === 5) s += ' lăm';
    else if (c > 0) s += ' ' + ONES[c];
  } else if (b === 0 && c > 0) {
    s += (s ? ' ' : '') + ONES[c];
  }
  return s.trim();
}

export function numberToVnWords(input) {
  let n = parseInt(input, 10);
  if (isNaN(n) || n === 0) return 'Không đồng';
  const negative = n < 0;
  n = Math.abs(n);
  const groups = [];
  while (n > 0) { groups.push(n % 1000); n = Math.floor(n / 1000); }
  const SUFFIX = ['', ' nghìn', ' triệu', ' tỷ', ' nghìn tỷ', ' triệu tỷ'];
  let s = '';
  for (let i = groups.length - 1; i >= 0; i--) {
    const g = groups[i];
    if (g === 0) continue;
    s += readThree(g, i !== groups.length - 1) + SUFFIX[i] + ' ';
  }
  s = s.trim() + ' đồng';
  s = s.charAt(0).toUpperCase() + s.slice(1);
  return (negative ? 'Âm ' : '') + s;
}

/**
 * Dòng «Bằng chữ» dưới ô Số tiền — hoặc '' khi không có gì để hiện.
 * Chỉ có bộ đọc số TIẾNG VIỆT (numberToVnWords, ra «… đồng») nên chỉ hiện khi tiền là VND **và**
 * app đang hiển thị tiếng Việt. Trước 12/10/2026 điều kiện chỉ xét VND ⇒ màn tiếng Anh/Pháp vẫn in
 * «Một triệu năm trăm nghìn đồng». Ngôn ngữ khác: bỏ dòng (Intl không có bộ đọc số thành chữ).
 */
export function amountInWords(value, currency) {
  if (value === '' || value == null) return '';
  if ((currency || 'VND') !== 'VND' || getLocale() !== 'vi') return '';
  return numberToVnWords(value);
}

/**
 * Eval visible_when condition. Trả true nếu field nên hiển thị.
 * visible_when: { field_id, operator, value }
 *  - operator: eq | neq | gt | lt | gte | lte | contains | empty | not_empty
 *
 * `operator` THIẾU thì hiểu là 'eq' — KHÔNG rơi xuống default. Trước 2026-08-28 ô
 * phép so sánh ở builder chỉ HIỆN "Bằng" mà không ghi khoá `operator` vào bản lưu
 * (chỉ ghi khi admin tự bấm đổi), nên mẫu lưu ra `{field_id, value}` → switch rơi
 * default → luôn true → điều kiện hiển thị CHẾT CÂM: trường phụ hiện ở mọi lúc,
 * không lỗi nào báo. Mặc định 'eq' ở đây chữa luôn mẫu đã lưu sai trong DB.
 */
export function evaluateVisible(visible_when, formData) {
  if (!visible_when || !visible_when.field_id) return true;
  const v = formData?.[visible_when.field_id];
  const target = visible_when.value;
  switch (visible_when.operator || 'eq') {
    case 'eq': return String(v ?? '') === String(target ?? '');
    case 'neq': return String(v ?? '') !== String(target ?? '');
    case 'gt': return Number(v) > Number(target);
    case 'lt': return Number(v) < Number(target);
    case 'gte': return Number(v) >= Number(target);
    case 'lte': return Number(v) <= Number(target);
    case 'contains':
      if (Array.isArray(v)) return v.includes(target);
      return String(v ?? '').includes(String(target ?? ''));
    case 'empty': return v == null || v === '' || (Array.isArray(v) && v.length === 0);
    case 'not_empty': return !(v == null || v === '' || (Array.isArray(v) && v.length === 0));
    default: return true;
  }
}

/**
 * Slugify label → identifier hợp lệ (lowercase, bỏ dấu tiếng Việt, _ thay khoảng trắng)
 * VD: "Tổng số tiền" → "tong_so_tien"
 */
export function slugifyLabel(s) {
  if (!s) return '';
  return String(s).toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Eval formula. Hỗ trợ: + - * / ( ), reference field bằng id hoặc tên (slugified), hàm sum/avg/min/max/round/abs.
 * Cẩn trọng: chỉ eval trong sandbox (with new Function), không truy cập window.
 */
export function evalFormula(expr, formFields, formData) {
  if (!expr || typeof expr !== 'string') return null;
  try {
    const idMap = {};
    formFields.forEach(f => {
      idMap[f.id] = Number(formData?.[f.id]) || 0;
      // alias by slugified label (bỏ dấu, snake_case)
      const alias = slugifyLabel(f.label);
      if (alias && idMap[alias] === undefined) idMap[alias] = idMap[f.id];
    });
    const safe = expr.replace(/[A-Za-z_][A-Za-z0-9_]*/g, (token) => {
      const lc = token.toLowerCase();
      if (['sum', 'avg', 'min', 'max', 'round', 'abs'].includes(lc)) return `__fn.${lc}`;
      if (idMap[token] !== undefined) return idMap[token];
      if (idMap[lc] !== undefined) return idMap[lc];
      return '0';
    });
    const __fn = {
      sum: (...a) => a.reduce((s, x) => s + Number(x), 0),
      avg: (...a) => a.length ? a.reduce((s, x) => s + Number(x), 0) / a.length : 0,
      min: Math.min, max: Math.max, round: Math.round, abs: Math.abs,
    };
    // eslint-disable-next-line no-new-func
    const fn = new Function('__fn', `return (${safe})`);
    const result = fn(__fn);
    return Number.isFinite(result) ? result : 0;
  } catch {
    return 0;
  }
}

// `currency`: mã tiền của ô công thức (withDefaultCurrency gắn theo tiền tệ workspace lúc dựng mẫu). Thiếu ⇒ VND
// (mẫu Work/Core và mẫu dựng trước 10/10/2026). Ký hiệu do Intl đặt theo ngôn ngữ đang hiển thị — không tự nối « đ».
export function formatFormulaValue(value, format, decimals = 0, currency) {
  if (value == null || isNaN(value)) return '—';
  const n = Number(value);
  switch (format) {
    case 'amount':
      return fmtCurrency(n, currency || 'VND', { d: decimals || 0 }, fmtNumber(n));
    case 'percent':
      return fmtNumber(n) + '%';
    case 'raw':
      return String(n);
    default:
      return fmtNumber(n);
  }
}
